import type { CSSProperties } from 'react'

/**
 * Brand tones for generated listing artwork (halftone covers, tag boxes,
 * tornado tiles). Values point at the landing tokens where one exists.
 */
export type ListingTone = 'blue' | 'pink' | 'purple' | 'green' | 'black'

export interface ToneSpec {
  /** Solid fill. */
  bg: string
  /** Text colour that holds contrast on the fill. */
  fg: string
  /** Halftone dot colour on the fill. */
  dot: string
}

export const TONES: Record<ListingTone, ToneSpec> = {
  blue: { bg: 'var(--color-landing-accent)', fg: '#ffffff', dot: 'rgba(255,255,255,0.42)' },
  pink: { bg: '#ff00f2', fg: '#0a0a0a', dot: 'rgba(255,255,255,0.5)' },
  purple: { bg: 'var(--color-landing-secondary)', fg: '#ffffff', dot: 'rgba(255,255,255,0.4)' },
  green: { bg: '#00d722', fg: '#0a0a0a', dot: 'rgba(255,255,255,0.55)' },
  black: { bg: '#0a0a0a', fg: '#ffffff', dot: 'rgba(255,255,255,0.28)' },
}

export const TONE_ORDER: ListingTone[] = ['blue', 'purple', 'pink', 'green', 'black']

/** Stable tone for an arbitrary string (used when content has no tone of its own). */
export function toneFor(key: string, palette: ListingTone[] = TONE_ORDER): ListingTone {
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  return palette[hash % palette.length]!
}

/** Two-letter monogram from a company or person name ("Chen & Associates Legal" → "CA"). */
export function monogram(name: string): string {
  const words = name
    .replace(/[^A-Za-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('')
}

/**
 * Inline styles for a halftone dot field: small dots plus large dots on the
 * same 8px grid (so the layers never beat into moiré), each masked toward one
 * corner, so dots read as growing across the tile.
 * `angle` picks the direction the coarse dots grow toward.
 */
export function halftoneLayers(dot: string, angle = 135): CSSProperties[] {
  return [
    {
      backgroundImage: `radial-gradient(circle, ${dot} 0 1.1px, transparent 1.5px)`,
      backgroundSize: '8px 8px',
      maskImage: `linear-gradient(${angle}deg, transparent 0%, #000 70%)`,
      WebkitMaskImage: `linear-gradient(${angle}deg, transparent 0%, #000 70%)`,
    },
    {
      backgroundImage: `radial-gradient(circle, ${dot} 0 2.5px, transparent 2.9px)`,
      backgroundSize: '8px 8px',
      maskImage: `linear-gradient(${angle}deg, transparent 45%, #000 100%)`,
      WebkitMaskImage: `linear-gradient(${angle}deg, transparent 45%, #000 100%)`,
    },
  ]
}
